import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	getStudentByIdOptions,
	getAttendancesByStudentOptions,
	getAllCoursesOptions,
	enrollStudentMutation,
	unenrollStudentMutation,
	cancelUnenrollStudentMutation,
	updateStudentMutation,
	getAllCoursesQueryKey,
	getStudentByIdQueryKey,
} from "@/lib/client/@tanstack/react-query.gen";
import type { Student } from "@/lib/client/types.gen";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import {
	ArrowLeft,
	Edit,
	Save,
	X,
	GraduationCap,
	Users,
	BookOpen,
	Calendar,
	UserMinus,
	CheckCircle2,
	XCircle,
	AlertCircle,
	Eye,
	School,
	UserPlus,
	History,
	ChevronDown,
	Archive,
} from "lucide-react";
import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import {
	getSubjectLabel,
	getLevelLabel,
	getSchoolLabel,
	getTimeSlot,
} from "@/stores/enum-store";
import { client } from "@/lib/client/client.gen";
import { updateEnrollmentDate } from "@/lib/client";
import { UnenrollDialog } from "@/components/UnenrollDialog";

export const Route = createFileRoute("/admin/students/$id")({
	component: StudentDetailsPage,
});

function StudentDetailsPage() {
	const { id } = Route.useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [pastEnrollmentsOpen, setPastEnrollmentsOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
	const [editEnrollmentDialogOpen, setEditEnrollmentDialogOpen] = useState(false);
	const [unenrollDialogOpen, setUnenrollDialogOpen] = useState(false);
	const [unenrollCourseId, setUnenrollCourseId] = useState("");
	const [unenrollCourseName, setUnenrollCourseName] = useState("");
	const [existingUnenrollDate, setExistingUnenrollDate] = useState<string | undefined>(undefined);
	const [selectedEnrollment, setSelectedEnrollment] = useState<any>(null);
	const [enrollmentDate, setEnrollmentDate] = useState("");
	const [selectedCourse, setSelectedCourse] = useState("");
	const [courseSearchTerm, setCourseSearchTerm] = useState("");

	const { data: student, isLoading: studentLoading } = useQuery({
		...getStudentByIdOptions({ path: { id } }),
	});

	const { data: allCoursesData } = useQuery(getAllCoursesOptions());
	const allCourses = (allCoursesData as any[]) || [];

	const updateStudentMut = useMutation({
		...updateStudentMutation(),
		onSuccess: () => {
			toast.success("Élève modifié avec succès");
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			queryClient.invalidateQueries({ queryKey: getStudentByIdQueryKey({path: {id}}) });
			setIsEditing(false);
		},
		onError: () => {
			toast.error("Erreur lors de la modification de l'élève");
		},
	});

	const enrollStudentMut = useMutation({
		...enrollStudentMutation(),
		onSuccess: () => {
			toast.success("Élève inscrit au cours avec succès");
			queryClient.invalidateQueries({ queryKey: getStudentByIdQueryKey({path: {id}}) });
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			setEnrollDialogOpen(false);
			setSelectedCourse("");
			setCourseSearchTerm("");
		},
		onError: () => {
			toast.error("Erreur lors de l'inscription au cours");
		},
	});

	const unenrollStudentMut = useMutation({
		...unenrollStudentMutation(),
		onSuccess: () => {
			toast.success("Élève désinscrit du cours");
			queryClient.invalidateQueries({ queryKey: getStudentByIdQueryKey({path: {id}}) });
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
		},
		onError: () => {
			toast.error("Erreur lors de la désinscription");
		},
	});

	const cancelUnenrollMut = useMutation({
		...cancelUnenrollStudentMutation(),
		onSuccess: () => {
			toast.success("Désinscription annulée");
			queryClient.invalidateQueries({ queryKey: getStudentByIdQueryKey({path: {id}}) });
			queryClient.invalidateQueries({ queryKey: getAllCoursesQueryKey() });
			setUnenrollDialogOpen(false);
			setUnenrollCourseId("");
			setUnenrollCourseName("");
			setExistingUnenrollDate(undefined);
		},
		onError: () => {
			toast.error("Erreur lors de l'annulation de la désinscription");
		},
	});

	const form = useForm({
		defaultValues: {
			firstName: student?.firstName || "",
			lastName: student?.lastName || "",
			hasPap: student?.hasPap || false,
			hasPps: student?.hasPps || false,
			school: student?.school || "SAINT_ANDRE",
			schoolOther: student?.schoolOther || "",
			level: student?.level || "CM1",
			className: student?.className || "",
		},
		onSubmit: async ({ value }) => {
			updateStudentMut.mutate({
				path: { id },
				body: {
					firstName: value.firstName,
					lastName: value.lastName,
					hasPap: value.hasPap,
					hasPps: value.hasPps,
					school: value.school as any,
					schoolOther: value.schoolOther,
					level: value.level as any,
					className: value.className,
				},
			});
		},
	});

	const handleOpenUnenrollDialog = (courseId: string, courseName: string, unrolledDate?: string) => {
		setUnenrollCourseId(courseId);
		setUnenrollCourseName(courseName);
		setExistingUnenrollDate(unrolledDate || undefined);
		setUnenrollDialogOpen(true);
	};

	const handleCancelUnenroll = () => {
		if (!unenrollCourseId) return;
		cancelUnenrollMut.mutate({
			path: {
				courseId: unenrollCourseId,
				studentId: id,
			},
		});
	};

	const handleUnenrollConfirm = (unenrollDate: string) => {
		if (!unenrollCourseId) return;
		unenrollStudentMut.mutate({
			path: {
				courseId: unenrollCourseId,
				studentId: id,
			},
			body: {
				unenrollDate,
			},
		});
		setUnenrollDialogOpen(false);
		setUnenrollCourseId("");
		setUnenrollCourseName("");
		setExistingUnenrollDate(undefined);
	};

	const handleEnroll = () => {
		if (!selectedCourse) {
			toast.error("Veuillez sélectionner un cours");
			return;
		}

		enrollStudentMut.mutate({
			path: {
				courseId: selectedCourse,
				studentId: id,
			},
			body: {
				enrollmentDate: enrollmentDate || new Date().toISOString().split("T")[0],
			},
		});
	};

	const handleUpdateEnrollmentDate = async () => {
		if (!selectedEnrollment || !enrollmentDate) {
			toast.error("Veuillez sélectionner une date");
			return;
		}

		try {
			await updateEnrollmentDate({
				body: {
					enrollmentDate,
				},
				path: { enrollmentId: selectedEnrollment.id }
			});
			toast.success("Date d'inscription modifiée avec succès");
			queryClient.invalidateQueries({ queryKey: getStudentByIdQueryKey({path: {id}}) });
			setEditEnrollmentDialogOpen(false);
			setSelectedEnrollment(null);
			setEnrollmentDate("");
		} catch (error) {
			toast.error("Erreur lors de la modification de la date");
		}
	};

	const dayOfWeekLabels: Record<string, string> = {
		MONDAY: "Lundi",
		TUESDAY: "Mardi",
		WEDNESDAY: "Mercredi",
		THURSDAY: "Jeudi",
		FRIDAY: "Vendredi",
		SATURDAY: "Samedi",
		SUNDAY: "Dimanche",
	};

	const attendanceStatusLabels: Record<
		string,
		{ label: string; icon: any; color: string }
	> = {
		PRESENT: { label: "Présent", icon: CheckCircle2, color: "text-green-600" },
		ABSENT: { label: "Absent", icon: XCircle, color: "text-red-600" },
		EXCUSED: { label: "Excusé", icon: AlertCircle, color: "text-orange-600" },
	};

	if (studentLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
					<p className="mt-4 text-gray-600">Chargement de l'élève...</p>
				</div>
			</div>
		);
	}

	if (!student) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<p className="text-gray-600">Élève non trouvé</p>
					<Button
						variant="outline"
						onClick={() => navigate({ to: "/admin/students" })}
						className="mt-4"
					>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Retour
					</Button>
				</div>
			</div>
		);
	}

	const enrolledCourses = student.enrollments || [];
	const pastEnrollments = student.pastEnrollments || [];
	const availableCourses = allCourses.filter(
		(course) => !enrolledCourses.some((ec) => ec.course.id === course.id),
	);

	const hasTimeSlotConflict = (courseTimeSlot: string | undefined) => {
		if (!courseTimeSlot) return false;
		return enrolledCourses.some(
			(ec) => ec.course.timeSlot === courseTimeSlot,
		);
	};

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Button
					variant="ghost"
					size="sm"
					onClick={() => navigate({ to: "/admin/students" })}
				>
					<ArrowLeft className="h-4 w-4 mr-2" />
					Retour
				</Button>
			</div>

			<div className="flex justify-between items-start">
				<div>
					<h1 className="text-3xl font-bold">
						{student.firstName} {student.lastName}
					</h1>
					<p className="text-gray-600 mt-2">
						{getLevelLabel(student.level)}
						{student.className && ` - ${student.className}`}
						{" - "}
						{getSchoolLabel(student.school)}
					</p>
				</div>
				{!isEditing ? (
					<Button onClick={() => setIsEditing(true)}>
						<Edit className="h-4 w-4 mr-2" />
						Modifier
					</Button>
				) : (
					<div className="flex gap-2">
						<Button variant="outline" onClick={() => setIsEditing(false)}>
							<X className="h-4 w-4 mr-2" />
							Annuler
						</Button>
						<Button
							onClick={() => form.handleSubmit()}
							disabled={updateStudentMut.isPending}
						>
							<Save className="h-4 w-4 mr-2" />
							{updateStudentMut.isPending ? "Enregistrement..." : "Enregistrer"}
						</Button>
					</div>
				)}
			</div>

			<Card>
				<CardHeader>
					<CardTitle>Informations de l'élève</CardTitle>
					<CardDescription>
						Détails et informations personnelles
					</CardDescription>
				</CardHeader>
				<CardContent>
					{!isEditing ? (
						<div className="grid grid-cols-2 gap-6">
							<div className="space-y-4">
								<div>
									<Label className="text-gray-500">Prénom</Label>
									<p className="font-medium">{student.firstName}</p>
								</div>
								<div>
									<Label className="text-gray-500">Nom</Label>
									<p className="font-medium">{student.lastName}</p>
								</div>
								<div>
									<Label className="text-gray-500">École</Label>
									<div className="flex items-center gap-2">
										<School className="h-4 w-4 text-gray-400" />
										<p className="font-medium">
											{getSchoolLabel(student.school)}
										</p>
									</div>
									{student.school === "AUTRE" && student.schoolOther && (
										<p className="text-sm text-gray-600 mt-1">
											{student.schoolOther}
										</p>
									)}
								</div>
							</div>
							<div className="space-y-4">
								<div>
									<Label className="text-gray-500">Niveau</Label>
									<div className="flex items-center gap-2">
										<GraduationCap className="h-4 w-4 text-gray-400" />
										<p className="font-medium">
											{getLevelLabel(student.level)}
										</p>
									</div>
								</div>
								{student.className && (
									<div>
										<Label className="text-gray-500">Classe</Label>
										<p className="font-medium">{student.className}</p>
									</div>
								)}
								<div>
									<Label className="text-gray-500">Besoins spécifiques</Label>
									<div className="flex gap-2 mt-1">
										{student.hasPap && <Badge variant="secondary">PAP</Badge>}
										{student.hasPps && <Badge variant="secondary">PPS</Badge>}
										{!student.hasPap && !student.hasPps && (
											<span className="text-gray-400 text-sm">Aucun</span>
										)}
									</div>
								</div>
								<div>
									<Label className="text-gray-500">Parent</Label>
									{student.parent ? (
										<div className="flex items-center gap-2 mt-1">
											<Users className="h-4 w-4 text-gray-400" />
											<Link
												to="/admin/parents/$id"
												params={{ id: student.parent.id || "" }}
											>
												<Button
													variant="link"
													className="p-0 h-auto font-medium"
												>
													{student.parent.firstName} {student.parent.lastName}
												</Button>
											</Link>
										</div>
									) : (
										<span className="text-gray-400 text-sm">
											Aucun parent rattaché
										</span>
									)}
								</div>
							</div>
						</div>
					) : (
						<form className="space-y-4">
							<div className="grid grid-cols-2 gap-4">
								<form.Field
									name="firstName"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Prénom</Label>
											<Input
												id={field.name}
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
											/>
										</div>
									)}
								/>
								<form.Field
									name="lastName"
									children={(field) => (
										<div>
											<Label htmlFor={field.name}>Nom</Label>
											<Input
												id={field.name}
												value={field.state.value}
												onChange={(e) => field.handleChange(e.target.value)}
											/>
										</div>
									)}
								/>
							</div>

							<form.Field
								name="school"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>École</Label>
										<Select
											value={field.state.value}
											onValueChange={(value) =>
												field.handleChange(value as any)
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="SAINT_ANDRE">Saint-André</SelectItem>
												<SelectItem value="AUTRE">Autre</SelectItem>
											</SelectContent>
										</Select>
									</div>
								)}
							/>

							<form.Field
								name="schoolOther"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>
											Nom de l'école (si autre)
										</Label>
										<Input
											id={field.name}
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											placeholder="Nom de l'école"
										/>
									</div>
								)}
							/>

							<form.Field
								name="level"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Niveau</Label>
										<Select
											value={field.state.value}
											onValueChange={(value) =>
												field.handleChange(value as any)
											}
										>
											<SelectTrigger>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="CM1">CM1</SelectItem>
												<SelectItem value="CM2">CM2</SelectItem>
												<SelectItem value="SIXIEME">Sixième</SelectItem>
												<SelectItem value="CINQUIEME">Cinquième</SelectItem>
												<SelectItem value="QUATRIEME">Quatrième</SelectItem>
												<SelectItem value="TROISIEME">Troisième</SelectItem>
												<SelectItem value="SECONDE">Seconde</SelectItem>
												<SelectItem value="PREMIERE">Première</SelectItem>
												<SelectItem value="TERMINALE">Terminale</SelectItem>
											</SelectContent>
										</Select>
									</div>
								)}
							/>

							<form.Field
								name="className"
								children={(field) => (
									<div>
										<Label htmlFor={field.name}>Classe</Label>
										<Input
											id={field.name}
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											placeholder="Ex: 6ème A, 3ème B, etc."
										/>
									</div>
								)}
							/>

							<div className="space-y-2">
								<Label>Besoins spécifiques</Label>
								<div className="flex gap-4">
									<form.Field
										name="hasPap"
										children={(field) => (
											<div className="flex items-center space-x-2">
												<Checkbox
													id={field.name}
													checked={field.state.value}
													onCheckedChange={(checked) =>
														field.handleChange(!!checked)
													}
												/>
												<label
													htmlFor={field.name}
													className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
												>
													PAP
												</label>
											</div>
										)}
									/>
									<form.Field
										name="hasPps"
										children={(field) => (
											<div className="flex items-center space-x-2">
												<Checkbox
													id={field.name}
													checked={field.state.value}
													onCheckedChange={(checked) =>
														field.handleChange(!!checked)
													}
												/>
												<label
													htmlFor={field.name}
													className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
												>
													PPS
												</label>
											</div>
										)}
									/>
								</div>
							</div>
						</form>
					)}
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<div className="flex justify-between items-center">
						<div>
							<CardTitle>Cours inscrits ({enrolledCourses.length})</CardTitle>
							<CardDescription>
								Liste des cours auxquels l'élève est inscrit
							</CardDescription>
						</div>
						<Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
							<DialogTrigger asChild>
								<Button>
									<UserPlus className="h-4 w-4 mr-2" />
									Inscrire à un cours
								</Button>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>Inscrire à un cours</DialogTitle>
									<DialogDescription>
										Sélectionnez le cours pour {student.firstName}{" "}
										{student.lastName}
									</DialogDescription>
								</DialogHeader>
								<div className="space-y-4">
									<div className="space-y-2">
										<Label htmlFor="enrollment-date">Date d'inscription (optionnel)</Label>
										<Input
											id="enrollment-date"
											type="date"
											value={enrollmentDate}
											onChange={(e) => setEnrollmentDate(e.target.value)}
										/>
										<p className="text-xs text-gray-500">
											Par défaut : aujourd'hui
										</p>
									</div>

									<div className="space-y-2">
										<Label htmlFor="course-search">Rechercher un cours</Label>
										<Input
											id="course-search"
											placeholder="Rechercher par matière, niveau, professeur..."
											value={courseSearchTerm}
											onChange={(e) => setCourseSearchTerm(e.target.value)}
										/>
										<div className="border rounded-md max-h-[300px] overflow-y-auto">
											{availableCourses
												.filter((course) => {
													if (!courseSearchTerm) return true;
													const searchLower = courseSearchTerm.toLowerCase();
													const timeSlot = getTimeSlot(course.timeSlot);
													return (
														course.subject?.toLowerCase().includes(searchLower) ||
														course.level?.toLowerCase().includes(searchLower) ||
														course.teacherName?.toLowerCase().includes(searchLower) ||
														dayOfWeekLabels[timeSlot?.dayOfWeek || ""]
															?.toLowerCase()
															.includes(searchLower)
													);
												})
												.map((course) => {
													const timeSlot = getTimeSlot(course.timeSlot);
													const hasConflict = hasTimeSlotConflict(course.timeSlot);
													const isSelected = selectedCourse === course.id;
													return (
														<div
															key={course.id}
															onClick={() => !hasConflict && setSelectedCourse(course.id || "")}
															className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
																hasConflict ? "opacity-50 cursor-not-allowed" : ""
															} ${isSelected ? "bg-blue-50 hover:bg-blue-100" : ""}`}
														>
															<div className="flex items-center justify-between">
																<div>
																	<p className="font-medium">
																		{getSubjectLabel(course.subject)} - {getLevelLabel(course.level)}
																	</p>
																	<p className="text-sm text-gray-600">
																		{dayOfWeekLabels[timeSlot?.dayOfWeek || ""]} {timeSlot?.startTime} - {timeSlot?.endTime}
																		{course.teacherName && ` - Prof: ${course.teacherName}`}
																	</p>
																	{hasConflict && (
																		<p className="text-xs text-red-600 mt-1">Conflit d'horaire</p>
																	)}
																</div>
																{isSelected && (
																	<div className="h-4 w-4 bg-blue-600 rounded-full flex items-center justify-center">
																		<div className="h-2 w-2 bg-white rounded-full"></div>
																	</div>
																)}
															</div>
														</div>
													);
												})}
										</div>
									</div>
								</div>
								<DialogFooter>
									<Button
										variant="outline"
										onClick={() => {
											setEnrollDialogOpen(false);
											setSelectedCourse("");
											setCourseSearchTerm("");
											setEnrollmentDate("");
										}}
									>
										Annuler
									</Button>
									<Button
										onClick={handleEnroll}
										disabled={enrollStudentMut.isPending}
									>
										{enrollStudentMut.isPending ? "Inscription..." : "Inscrire"}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>

						{/* Modal de désinscription */}
						<UnenrollDialog
							open={unenrollDialogOpen}
							onOpenChange={setUnenrollDialogOpen}
							studentName={student ? `${student.firstName} ${student.lastName}` : ""}
							courseName={unenrollCourseName}
							isPending={unenrollStudentMut.isPending}
							isCancelPending={cancelUnenrollMut.isPending}
							existingUnenrollDate={existingUnenrollDate}
							onConfirm={handleUnenrollConfirm}
							onCancelUnenroll={existingUnenrollDate ? handleCancelUnenroll : undefined}
						/>

						{/* Modal de modification de la date d'inscription */}
						<Dialog open={editEnrollmentDialogOpen} onOpenChange={setEditEnrollmentDialogOpen}>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>Modifier la date d'inscription</DialogTitle>
									<DialogDescription>
										Modifiez la date d'inscription de {student.firstName}{" "}
										{student.lastName} au cours
									</DialogDescription>
								</DialogHeader>
								<div className="space-y-4">
									{selectedEnrollment && (
										<div className="bg-gray-50 p-4 rounded-lg">
											<p className="font-medium">
												{getSubjectLabel(selectedEnrollment.course.subject)} -{" "}
												{getLevelLabel(selectedEnrollment.course.level)}
											</p>
											<p className="text-sm text-gray-600">
												{dayOfWeekLabels[
													getTimeSlot(selectedEnrollment.course.timeSlot)?.dayOfWeek || ""
												]}{" "}
												{getTimeSlot(selectedEnrollment.course.timeSlot)?.startTime} -{" "}
												{getTimeSlot(selectedEnrollment.course.timeSlot)?.endTime}
											</p>
										</div>
									)}

									<div className="space-y-2">
										<Label htmlFor="edit-enrollment-date">Date d'inscription</Label>
										<Input
											id="edit-enrollment-date"
											type="date"
											value={enrollmentDate}
											onChange={(e) => setEnrollmentDate(e.target.value)}
										/>
									</div>
								</div>
								<DialogFooter>
									<Button
										variant="outline"
										onClick={() => {
											setEditEnrollmentDialogOpen(false);
											setSelectedEnrollment(null);
											setEnrollmentDate("");
										}}
									>
										Annuler
									</Button>
									<Button
										onClick={handleUpdateEnrollmentDate}
									>
										Enregistrer
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					</div>
				</CardHeader>
				<CardContent>
					{enrolledCourses.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Cours</TableHead>
									<TableHead>Niveau</TableHead>
									<TableHead>Jour et horaire</TableHead>
									<TableHead>École</TableHead>
									<TableHead>Professeur</TableHead>
									<TableHead>Date d'inscription</TableHead>
									<TableHead>Actions</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{enrolledCourses.map((enrolment) => (
									<TableRow key={enrolment.id}>
										<TableCell>
											<div className="flex items-center gap-2">
												<BookOpen className="h-4 w-4 text-blue-600" />
												<span className="font-medium">
													{getSubjectLabel(enrolment.course.subject)}
												</span>
											</div>
										</TableCell>
										<TableCell>
											{getLevelLabel(enrolment.course.level)}
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2">
												<Calendar className="h-4 w-4 text-gray-400" />
												<span className="text-sm">
													{
														dayOfWeekLabels[
															getTimeSlot(enrolment.course.timeSlot)
																?.dayOfWeek || ""
														]
													}{" "}
													{getTimeSlot(enrolment.course.timeSlot)?.startTime} -{" "}
													{getTimeSlot(enrolment.course.timeSlot)?.endTime}
												</span>
											</div>
										</TableCell>
										<TableCell>
											{getSchoolLabel(enrolment.course.school)}
										</TableCell>
										<TableCell>
											<span className="text-sm">
												{enrolment.course.teacherName || "-"}
											</span>
										</TableCell>
										<TableCell>
											<div className="space-y-1">
												<span className="text-sm">
													{enrolment.enrollmentDate
														? new Date(enrolment.enrollmentDate).toLocaleDateString("fr-FR")
														: "-"}
												</span>
												{enrolment.unrolledDate && (
													<div>
														<Badge variant="outline" className="text-xs text-orange-600 border-orange-300 bg-orange-50">
															Désinscription le {new Date(enrolment.unrolledDate).toLocaleDateString("fr-FR")}
														</Badge>
													</div>
												)}
											</div>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2">
												<Link
													to="/admin/courses/$id"
													params={{ id: enrolment.course.id }}
												>
													<Button
														size="sm"
														variant="outline"
														title="Voir le cours"
													>
														<Eye className="h-3 w-3" />
													</Button>
												</Link>
												<Button
													size="sm"
													variant="outline"
													onClick={() => {
														setSelectedEnrollment(enrolment);
														setEnrollmentDate(enrolment.enrollmentDate || "");
														setEditEnrollmentDialogOpen(true);
													}}
													title="Modifier l'inscription"
												>
													<Edit className="h-3 w-3" />
												</Button>
												<Button
													size="sm"
													variant="ghost"
													onClick={() =>
														handleOpenUnenrollDialog(
															enrolment.course.id,
															`${getSubjectLabel(enrolment.course.subject)} - ${getLevelLabel(enrolment.course.level)}`,
															enrolment.unrolledDate,
														)
													}
													disabled={unenrollStudentMut.isPending}
													title={enrolment.unrolledDate ? "Modifier la désinscription" : "Désinscrire"}
												>
													<UserMinus className={`h-3 w-3 ${enrolment.unrolledDate ? "text-orange-500" : ""}`} />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					) : (
						<div className="text-center py-12">
							<BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
							<p className="text-gray-500 font-medium">Aucun cours inscrit</p>
							<p className="text-gray-400 text-sm mt-1">
								Cliquez sur "Inscrire à un cours" pour ajouter des cours
							</p>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Anciennes inscriptions (terminées ou clôturées par l'archivage d'un cours) */}
			<Collapsible open={pastEnrollmentsOpen} onOpenChange={setPastEnrollmentsOpen}>
				<Card>
					<CollapsibleTrigger asChild>
						<CardHeader className="cursor-pointer select-none">
							<div className="flex items-center justify-between">
								<div>
									<CardTitle className="flex items-center gap-2">
										<History className="h-5 w-5 text-gray-500" />
										Anciennes inscriptions ({pastEnrollments.length})
									</CardTitle>
									<CardDescription>
										Cours que l'élève a suivis par le passé (désinscriptions et
										cours archivés)
									</CardDescription>
								</div>
								<ChevronDown
									className={`h-5 w-5 text-gray-400 transition-transform ${pastEnrollmentsOpen ? "rotate-180" : ""}`}
								/>
							</div>
						</CardHeader>
					</CollapsibleTrigger>
					<CollapsibleContent>
						<CardContent>
							{pastEnrollments.length > 0 ? (
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Cours</TableHead>
											<TableHead>Établissement</TableHead>
											<TableHead>Période</TableHead>
											<TableHead>Statut du cours</TableHead>
											<TableHead>Actions</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{pastEnrollments.map((enrolment) => (
											<TableRow key={enrolment.id} className="text-gray-600">
												<TableCell>
													<div className="flex items-center gap-2">
														<BookOpen className="h-4 w-4 text-gray-400" />
														<span className="font-medium">
															{getSubjectLabel(enrolment.course.subject)} -{" "}
															{getLevelLabel(enrolment.course.level)}
														</span>
													</div>
												</TableCell>
												<TableCell className="text-sm">
													{getSchoolLabel(enrolment.course.school)}
												</TableCell>
												<TableCell className="text-sm">
													{enrolment.enrollmentDate
														? new Date(enrolment.enrollmentDate).toLocaleDateString("fr-FR")
														: "?"}{" "}
													→{" "}
													{enrolment.unrolledDate
														? new Date(enrolment.unrolledDate).toLocaleDateString("fr-FR")
														: "?"}
												</TableCell>
												<TableCell>
													{enrolment.course.archivedAt ? (
														<Badge
															variant="outline"
															className="text-xs text-gray-600 border-gray-300 bg-gray-100"
														>
															<Archive className="h-3 w-3 mr-1" />
															Cours archivé
														</Badge>
													) : (
														<Badge
															variant="outline"
															className="text-xs text-orange-600 border-orange-300 bg-orange-50"
														>
															Désinscrit
														</Badge>
													)}
												</TableCell>
												<TableCell>
													<Link
														to="/admin/courses/$id"
														params={{ id: enrolment.course.id }}
													>
														<Button size="sm" variant="outline" title="Voir le cours">
															<Eye className="h-3 w-3" />
														</Button>
													</Link>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							) : (
								<div className="text-center py-8 text-gray-500">
									<History className="mx-auto h-8 w-8 text-gray-400 mb-2" />
									<p className="text-sm">Aucune ancienne inscription</p>
								</div>
							)}
						</CardContent>
					</CollapsibleContent>
				</Card>
			</Collapsible>

			<Card>
				<CardHeader>
					<CardTitle>Présences / Absences</CardTitle>
					<CardDescription>Historique des présences de l'élève</CardDescription>
				</CardHeader>
				<CardContent>
					{studentLoading ? (
						<div className="text-center py-12">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
							<p className="mt-4 text-gray-600">Chargement des présences...</p>
						</div>
					) : student && student.attendances.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Date</TableHead>
									<TableHead>Cours</TableHead>
									<TableHead>Statut</TableHead>
									<TableHead>Notes</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{student.attendances.map((attendance) => {
									const statusInfo = attendanceStatusLabels[
										attendance.status
									] || {
										label: attendance.status,
										icon: AlertCircle,
										color: "text-gray-600",
									};
									const StatusIcon = statusInfo.icon;

									return (
										<TableRow key={attendance.id}>
											<TableCell>
												<div className="flex items-center gap-2">
													<Calendar className="h-4 w-4 text-gray-400" />
													{new Date(attendance.date).toLocaleDateString(
														"fr-FR",
													)}
												</div>
											</TableCell>
											<TableCell>
												{attendance.course ? (
													<Link
														to="/admin/courses/$id"
														params={{ id: attendance.course.id || "" }}
													>
														<Button variant="link" className="p-0 h-auto">
															{getSubjectLabel(attendance.course.subject)} -{" "}
															{getLevelLabel(attendance.course.level)}
														</Button>
													</Link>
												) : (
													<span className="text-gray-400">-</span>
												)}
											</TableCell>
											<TableCell>
												<div
													className={`flex items-center gap-2 ${statusInfo.color}`}
												>
													<StatusIcon className="h-4 w-4" />
													<span>{statusInfo.label}</span>
												</div>
											</TableCell>
											<TableCell className="text-sm text-gray-600">
												{attendance.notes || "-"}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					) : (
						<div className="text-center py-12">
							<CheckCircle2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
							<p className="text-gray-500 font-medium">
								Aucune présence enregistrée
							</p>
							<p className="text-gray-400 text-sm mt-1">
								Les présences peuvent être enregistrées depuis la page de
								gestion des présences
							</p>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
